export type GameType = 'pokemon' | 'mtg' | 'one-piece-card-game' | 'riftbound-league-of-legends-trading-card-game'

export interface ISlotConfig {
    bucket: string
    trueRarityChances?: Record<string, number>
    customCardIds?: string[]
}

export interface IPackSlotItem {
    _id: string
    id: string
    cardId: string
    name: string
    image: string
    rarity: string
    chance: number
    tickets: { start: number; end: number }
    averagePrice: { usd: number }
    extra_data?: Record<string, unknown>
}

export interface IPackSlot {
    index: number
    bucket: string
    trueRarityChances?: Record<string, number>
    items: IPackSlotItem[]
}

export interface IPackCategory {
    _id: string
    name: string
    slug: string
    image?: string
    createdAt?: string
    updatedAt?: string
}

export interface IPack {
    _id: string
    slug: string
    name: string
    game: GameType
    setCodes: string[]
    banned?: string[]
    price: { usd: number }
    /** Integer cents. The floor the generated `price` is raised to. */
    minPrice?: number
    opened: number
    hidden: boolean
    freePackPool: boolean
    slots?: IPackSlot[]
    categories?: string[]
    /**
     * The blueprint's `updatedAt` at the moment this pack was last generated. Compare
     * it with the blueprint's current `updatedAt` to tell whether the recipe has been
     * edited since the price was computed. Absent on packs last generated before the
     * field existed, which is unknown rather than stale. See `needsGenerate`.
     */
    blueprintUpdatedAt?: string
    createdAt?: string
    updatedAt?: string
    hasCustomPackSet?: boolean
}

export interface ICustomPackSet {
    _id: string
    slug: string
    name?: string
    image?: string
    flatImage?: string
    game: GameType
    setCodes: string[]
    banned: string[]
    slots: Record<string, ISlotConfig>
    categoryIds?: string[]
    /**
     * Integer cents: the floor under the price that generation computes from the
     * slots' expected value. Stored on the blueprint and copied onto the pack on every
     * `force-create`, so it has to survive every blueprint save -- the endpoint writes
     * `minPrice ?? 0` as part of a full replace, and omitting it resets the floor.
     */
    minPrice?: number
    hidden?: boolean
    createdAt?: string
    updatedAt?: string
    pack: IPack | null
}

export interface ISetCodeInfo {
    game: GameType
    setCode: string
    set: string
    cardCount: number
}

export interface ICustomPackSetsResponse {
    success: boolean
    entries: ICustomPackSet[]
}

export interface IPacksListResponse {
    success: boolean
    packs: IPack[]
    count: number
    page: number
    limit: number
    totalPages: number
}

export interface ISetCodesResponse {
    success: boolean
    setCodes: ISetCodeInfo[]
}

export interface IRarityInfo {
    rarity: string
    normalized: string
    count: number
    sources: { setCodes: number; customCards: number }
}

export interface IRaritiesResponse {
    success: boolean
    rarities: IRarityInfo[]
}

export interface IUpsertResponse {
    success: boolean
    message: string
    entry: ICustomPackSet
    livePackUpdated?: boolean
}

export interface ICategoryListResponse {
    success: boolean
    categories: IPackCategory[]
}

export interface ICategoryUpsertResponse {
    success: boolean
    message: string
    category: IPackCategory
}

export interface ICategoryDeleteResponse {
    success: boolean
    message: string
}

export interface IForceCreateResponse {
    success: boolean
    message: string
    results: Record<string, string>
}

export interface IToggleFreePackPoolResponse {
    success: boolean
    message: string
    pack: IPack
}

export type PackMode = 'set' | 'mixed' | 'custom'

export const GAME_OPTIONS: { label: string; value: GameType }[] = [
    { label: 'Pokémon', value: 'pokemon' },
    { label: 'Magic: The Gathering', value: 'mtg' },
    { label: 'One Piece', value: 'one-piece-card-game' },
    { label: 'Riftbound', value: 'riftbound-league-of-legends-trading-card-game' }
]

export function derivePackMode(setCodes: string[], slots: Record<string, ISlotConfig>): PackMode {
    const hasSets = setCodes.length > 0
    const anyCustom = Object.values(slots).some(s => (s.customCardIds?.length ?? 0) > 0)
    if (!hasSets) return 'custom'
    return anyCustom ? 'mixed' : 'set'
}

/**
 * Whether the live pack is known to have been built from an older revision of this
 * recipe, so the price and card list it is showing are the previous ones.
 *
 * Generating stamps the blueprint's `updatedAt` onto the pack; saving the blueprint
 * moves the blueprint's own and leaves the stamp where it was. The pack's own
 * `updatedAt` cannot answer this, because opening a pack and the visibility and
 * free-pool toggles all move it without repricing anything.
 *
 * A missing stamp is unknown, not stale. Packs last generated before the stamp existed
 * have none, and reading those as stale marks every pack at once, which says nothing
 * about any of them. It corrects itself the first time each one is generated.
 */
export function needsGenerate(entry: ICustomPackSet): boolean {
    const generatedFrom = entry.pack?.blueprintUpdatedAt
    if (!generatedFrom || !entry.updatedAt) return false
    return Date.parse(generatedFrom) !== Date.parse(entry.updatedAt)
}

export function slugify(input: string): string {
    return input
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

/**
 * The slug the endpoint will store, which is also the identity it upserts on: an
 * explicit slug wins, then the set codes, then the name.
 *
 * The slug has to beat the set codes, or two blueprints on the same sets derive the
 * same slug and the second one replaces the first.
 */
export function previewSlug(setCodes: string[], slug?: string, name?: string): string {
    const explicit = slugify(slug || '')
    if (explicit) {
        return `pack-${explicit}`
    }
    const cleanSetCodes = Array.from(new Set(setCodes.map(s => s.toLowerCase().trim()).filter(Boolean))).sort()
    if (cleanSetCodes.length > 0) {
        return `pack-${cleanSetCodes.join('-')}`
    }
    const base = slugify(name || '')
    return base ? `pack-${base}` : ''
}
